import React, { FC, useState, ReactNode, useEffect, useRef } from "react";
import { Divider, TextField, Tooltip } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import MButton from "components/momentum/MButton";
import { useAxios } from "myAxios";
import ArrowPopper from "components/app/ArrowPopper";
import { GirdViewFunction } from "../GridViewFunctionBar";
import { dispatchCustomEvent } from "utils";
import { LabelData } from "../SaveSearchPanel";
import { FILTER_TITLE_MAPPING, KEYWORD_MAPPING, KEYWORD_SYMBOL_MAPPING } from "constant";

const useStyles = makeStyles(() => ({
  popHeader: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#009ECB",
  },
  popBody: {
    marginTop: "14px",
  },
  popTop: {
    color: "#333",
    marginTop: "16px",
    listStyleType: "none",
  },
  tip: {
    fontSize: 10,
  },
  detail: {
    color: "#535759",
    marginTop: "14px",
  },
  popBottom: {
    fontSize: 14,
    color: "#343537",
    marginTop: "14px",
  },
  input: {
    margin: "14px 0 6px 0",
  },
  bottom: {
    marginTop: "24px",
    display: "flex",
    justifyContent: "flex-end",
  },
  disabled: {
    background: "#D2D5D6",
    color: "#B4B6B8",
  },
  dismiss: {
    marginRight: "16px",
  },
  redStar: {
    color: "red",
  },
}));

const SaveSearch: FC<
  GirdViewFunction & {
    disablePortal?: boolean;
  }
> = ({ anchorEl, open, onClose, onClick, ready, pageId, gridEvent, disablePortal = false }) => {
  //const { gridEvent } = useContext(GridEventProvider);
  const axios = useAxios();
  const classes = useStyles();
  const [saveName, setSaveName] = useState("");
  const [selectList, setSelectList] = useState<ReactNode>("");
  // const [selectList, setSelectList] = useState("");
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [saveButtonDisabled, setSaveButtonDisabled] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const epPopRef = useRef<HTMLDivElement>(null);
  const epCellRef = useRef<HTMLDivElement>(null);
  const [showTooltip, setShowTooltip] = useState(false);
  // const fieldMapping: { id: string; name: string }[] = [];
  const updatePage = () => {
    const filterList = gridEvent?.api.getFilterModel() || {};
    if (filterList && Object.keys(filterList).length > 0) {
      const list = renderList(filterList);
      setSelectList(list);
    }
  };

  useEffect(() => {
    if (saveName) {
      setSaveButtonDisabled(false);
    } else {
      setSaveButtonDisabled(true);
    }
  }, [saveName]);

  // open page render page
  useEffect(() => {
    if (gridEvent && open) {
      updatePage();
    }
  }, [gridEvent, open]);

  useEffect(() => {
    const closePop = (e: Event) => {
      if (
        open &&
        !epPopRef.current?.contains(e.target as Node) &&
        epPopRef.current !== e.target &&
        !epCellRef.current?.contains(e.target as Node)
      ) {
        onClose();
      }
    };
    document.addEventListener("click", closePop);
    return () => {
      document.removeEventListener("click", closePop);
    };
  }, [open]);

  const getHeaderName = (colId: string) => {
    return gridEvent?.api.getColumnDef(colId)?.headerName ?? colId;
  };

  const handleButtonStatus = () => {
    const {
      externalFilter: _externalFilter,
      externalToggle: _externalToggle,
      externalInactiveSite: _externalInactiveSite,
      // externalAlertFilter: _externalAlertFilter,
      ...filterModel
    } = gridEvent!.api.getFilterModel();
    if (Object.keys(filterModel).length > 0) {
      for (const key of Object.keys(filterModel)) {
        if (key === "externalSearch") {
          const [_include, ...values] = filterModel[key].filter.split("%|%");
          if (values.length && values[0]) {
            setButtonDisabled(false);
          } else {
            setButtonDisabled(true);
          }
        } else {
          // if only expand the sidebar filter,not apply,it also can get filtermodel
          // so need to decide filter !== ""
          if (!!filterModel[key].filter || filterModel[key].filter === 0) {
            setButtonDisabled(false);
          } else {
            setButtonDisabled(true);
          }
        }
      }
    } else {
      setButtonDisabled(true);
    }
  };

  // when filter change, update page
  useEffect(() => {
    if (gridEvent) {
      gridEvent.api.addEventListener("filterChanged", () => {
        updatePage();
        handleButtonStatus();
      });
      // run when page init
      updatePage();
      handleButtonStatus();
    }
  }, [gridEvent]);

  const renderList = (filterList: {
    [key: string]: { filter: string; type: keyof typeof KEYWORD_MAPPING };
  }) => {
    // let list = "";
    // for ( const key of Object.keys( filterList ) )

    return Object.keys(filterList).map((key) => {
      // const filterTitle = (key.charAt(0).toUpperCase() + key.slice(1))
      //   .replace(/([A-Z])/g, " $1")
      //   .trim();
      const filterTitle = getHeaderName(key);
      // const filterTitle = field
      //   ? field.name
      //   : (key.charAt(0).toUpperCase() + key.slice(1))
      //       .replace(/([A-Z])/g, " $1")
      //       .trim();
      const filterVal = filterList[key].filter;
      const filterType = filterList[key].type;
      const symbol = KEYWORD_SYMBOL_MAPPING[filterType];
      if (filterVal !== "") {
        if (key.startsWith("accountSegmentation")) {
          return (
            <li key={key}>
              {" "}
              ·{" "}
              {`${
                FILTER_TITLE_MAPPING[filterTitle as keyof typeof FILTER_TITLE_MAPPING] ??
                filterTitle
              } (Segmentation) ${symbol} ${filterVal?.toString().substr(0, 30)} ${
                filterVal?.length > 30 ? "..." : ""
              }`}
            </li>
          );
        }
        switch (key) {
          case "externalKeyword":
            break;
          case "externalAdvancedFilters":
            break;
          // case "externalAlertFilter":
          //   break;
          case "externalSearch":
            const [_includeInactive, ...criteria] = filterVal.split("%|%");
            const keyword = filterList?.["externalKeyword"]?.filter || criteria.join("%|%");
            if (!criteria.length || keyword === "") return null;
            return (
              <li key={key}>
                {" "}
                ·<span style={{ textTransform: "capitalize" }}> keyword</span>
                {` = ${keyword}`}
              </li>
            );
          case "externalInactiveSite":
            return <li key={key}> · {`${filterTitle} ${symbol} ${filterVal === "false"}`}</li>;
          case "arOffPCCount":
            return (
              <li key={key}> · {`${filterTitle} ${symbol} ${filterVal === "1" ? "Yes" : "No"}`}</li>
            );
          case "partnerAccountName":
            return (
              <li key={key}>
                {" "}
                ·{" "}
                {`${filterTitle} ${symbol} ${filterVal
                  ?.replace(/(Non-){0,1}Lifecycle%-%/g, "")
                  .substr(0, 30)} ${filterVal?.length > 30 ? "..." : ""}`}
              </li>
            );
          case "renewalDate":
            const [from, to] = filterVal?.split("|");
            return <li key={key}> · {`${filterTitle} ${symbol} from ${from} to ${to}`}</li>;
          case "foundationalSetup-hasClaimedDomain":
            return (
              <li key={key}>
                {" "}
                ·{" "}
                {`${
                  FILTER_TITLE_MAPPING[filterTitle as keyof typeof FILTER_TITLE_MAPPING] ??
                  filterTitle
                } ${symbol} ${filterVal} 
              `}
              </li>
            );
          case "collabMapUploadedDate":
            return (
              <li key={key}>
                {" "}
                ·{" "}
                {`${
                  FILTER_TITLE_MAPPING[filterTitle as keyof typeof FILTER_TITLE_MAPPING] ??
                  filterTitle
                } ${filterType === "notEquals" ? "=" : symbol} ${filterVal} 
                `}
              </li>
            );

          default:
            const val = filterVal?.toString().replaceAll("null", "None");
            return (
              <li key={key}>
                {" "}
                ·{" "}
                {`${
                  FILTER_TITLE_MAPPING[filterTitle as keyof typeof FILTER_TITLE_MAPPING] ??
                  filterTitle
                } ${symbol} ${val?.toString().substr(0, 30)} ${val?.length > 30 ? "..." : ""}`}
              </li>
            );
        }
      }
    });
    // return list;
  };

  // const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
  //   setAnchorEl((el) => (!!el ? null : event.currentTarget));
  // };
  const handleClose = () => {
    onClose();
  };
  const saveFilter = () => {
    // call save filter api
    let postData = {
      name: saveName,
      type: pageId,
      data: "",
    };
    const {
      //  externalAlertFilter: _externalAlertFilter,
      externalAdvancedFilters: _externalAdvancedFilters,
      ...filterList
    } = gridEvent?.api.getFilterModel() || {};
    const data = JSON.stringify(filterList);
    postData = { ...postData, data };
    axios
      .post<string>(`/api/portfolio/savedSearch`, postData)
      .then((res) => {
        dispatchCustomEvent<{ labelData: LabelData }>("SAVE_SEARCH", {
          labelData: { id: res.data, name: postData.name, data: postData.data },
        });
        setSaveName("");
        // then close
        handleClose();
      })
      .catch((e) => {
        setErrorMessage(e.response?.data?.message ?? "Save failed");
      });
  };
  const limitMaxLength = (e: any) => {
    let val = e.target.value;
    if (val.length > 25) {
      val = val.slice(0, 25);
      setErrorMessage("Name of search can be up to 25 characters including spaces");
    } else {
      setErrorMessage("");
    }
    setSaveName(val);
  };

  return (
    <div>
      <Tooltip
        disableHoverListener={!buttonDisabled}
        disableFocusListener={!buttonDisabled}
        disableTouchListener={!buttonDisabled}
        open={showTooltip}
        arrow={true}
        title={
          <div>
            <b>Disabled until searched for accounts and/or applied any of the side view filters</b>
          </div>
        }
      >
        <span
          style={{ marginLeft: 5 }}
          ref={epCellRef}
          onMouseOverCapture={buttonDisabled ? () => setShowTooltip(true) : undefined}
          onMouseOutCapture={buttonDisabled ? () => setShowTooltip(false) : undefined}
        >
          <MButton
            onClick={onClick}
            active={open}
            size="small"
            disabled={buttonDisabled || !ready}
          >
            Save Search
          </MButton>
        </span>
      </Tooltip>

      <ArrowPopper
        open={open}
        anchorEl={anchorEl}
        disableFlip={true}
        disablePortal={disablePortal}
        pageId={pageId}
        // onClose={handleClose}
      >
        <div ref={epPopRef}>
          <div className={classes.popHeader}>
            <h4>Save Current Search</h4>
          </div>
          <div className={classes.popBody}>
            <div className={classes.popTop}>
              <p>Please review filter criteria about to be saved</p>
              {selectList}
            </div>
            <Divider />
            <div className={classes.popBottom}>
              <p>
                <span className={classes.redStar}>* </span>
                Add descriptive name for your favourite search:
              </p>
              <TextField
                variant="outlined"
                size="small"
                className={classes.input}
                onChange={limitMaxLength}
                value={saveName}
                required={true}
                error={!!errorMessage}
                helperText={errorMessage}
                fullWidth={true}
              />
            </div>
          </div>
          <div className={classes.bottom}>
            <MButton
              onClick={handleClose}
              color="primary"
              size="small"
              className={classes.dismiss}
            >
              Dismiss
            </MButton>
            <MButton
              variant="contained"
              color="primary"
              size="small"
              onClick={saveFilter}
              disabled={saveButtonDisabled}
              trackName="Save Search"
            >
              Save
            </MButton>
          </div>
        </div>
      </ArrowPopper>
    </div>
  );
};

export default SaveSearch;
